import type { Community } from '@communities/community/domain';
import { CommunityHeader, HeroImage } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { CommunityAuditAccuracyChart } from '@internal-audit/components/CommunityAuditAccuracyChart';
import { CommunityAuditLabelAnalysis } from '@internal-audit/components/CommunityAuditLabelAnalysis';
import { CommunityAuditPerformanceMetrics } from '@internal-audit/components/CommunityAuditPerformanceMetrics';
import { CommunityAuditProductionStatus } from '@internal-audit/components/CommunityAuditProductionStatus';
import type { AuditReport } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useGetAuditReportsByDateRange } from '@internal-audit/hooks/useGetAuditReportsByDateRange';
import type { ComprehensiveAnalytics } from '@internal-audit/hooks/useGetComprehensiveAnalytics';
import { useGetComprehensiveAnalytics } from '@internal-audit/hooks/useGetComprehensiveAnalytics';
import { Heading } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import * as styles from './CommunityAuditHeaderSection.css';

interface CommunityAuditHeaderSectionProps {
  community: Community | null;
  auditSessions: FlexibleAuditSession[];
  auditReports?: AuditReport[];
  isLoading?: boolean;
}

/**
 * Audit Analytics Panels Grid - can be used in header (V1) or page body (V2)
 * Uses the unified ComprehensiveAnalytics interface from the hook that matches the GraphQL query.
 */
export interface AuditAnalyticsPanelsProps {
  auditSessions: FlexibleAuditSession[];
  auditReports: AuditReport[];
  comprehensiveAnalytics: ComprehensiveAnalytics | null;
  isLoading?: boolean;
}

export function AuditAnalyticsSection({ auditSessions, auditReports, comprehensiveAnalytics, isLoading = false }: AuditAnalyticsPanelsProps) {
  return (
    <div className={styles.analyticsSection}>
      {/* 4-Panel Grid Layout */}
      <div className={styles.fourPanelContainer}>
        <div className={styles.fourPanelGrid}>
          {/* Performance Metrics Over Time Chart Panel */}
          <CommunityAuditAccuracyChart auditReports={auditReports} auditSessions={auditSessions} isLoading={isLoading} />

          {/* Performance Metrics Bar Chart Panel */}
          <CommunityAuditPerformanceMetrics comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />

          {/* Label Analysis Panel */}
          <CommunityAuditLabelAnalysis comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />

          {/* Audit History Panel - Now Last */}
          <CommunityAuditProductionStatus auditSessions={auditSessions} comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />

          {/* Consolidated Footer for Analytics Panels */}
          <div className={styles.analyticsFooter}>
            <span className={styles.analyticsFooterText}>Averaged across all completed audit reports (max 100)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Header section component for audit pages with gradient transition
 * Contains community name, image, and accuracy over time graph
 * Background image is handled by the global useHeroBackground hook
 */
// eslint-disable-next-line complexity
function CommunityAuditHeaderSectionComponent({ community, auditSessions, auditReports, isLoading = false }: CommunityAuditHeaderSectionProps) {
  const name = community?.name || '';

  // Calculate date range for the last 1 year to get meaningful chart data
  const dateRange = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();

    fromDate.setFullYear(fromDate.getFullYear() - 1); // Last 1 year

    return {
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
    };
  }, []);

  // Fetch audit reports by date range for the chart
  const { auditReports: auditReportsByDateRange, loading: auditReportsLoading } = useGetAuditReportsByDateRange({
    communityId: community?.id,
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    options: {
      skip: !community?.id,
    },
  });

  // Fetch comprehensive analytics for the same time period
  const { comprehensiveAnalytics, isLoading: analyticsIsLoading } = useGetComprehensiveAnalytics({
    communityId: community?.id || '',
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    skip: !community?.id,
  });

  // Use the fetched audit reports for the chart, fallback to prop if provided
  const chartAuditReports = auditReportsByDateRange.length > 0 ? auditReportsByDateRange : auditReports || [];

  // Coordinate loading states - all panels should load together
  const anyDataLoading = isLoading || auditReportsLoading || analyticsIsLoading;
  const chartIsLoading = anyDataLoading;
  const analyticsComponentsLoading = anyDataLoading;

  // V1: Original implementation with analytics in header
  const v1Component = (
    <div className={styles.heroSection} data-testid="community-audit-header-v1">
      {/* Back Button Placeholder - Invisible but takes up same space as detail pages for consistent positioning */}
      <div className={styles.backButtonPlaceholder} />

      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Image */}
        <HeroImage isLoading={isLoading} logoUrl={community?.logoUrl} name={name} />

        {/* Right Column - Content */}
        <div className={styles.heroContentRight}>
          {/* Community Title */}
          <Heading className={styles.communityTitle} level="h1">
            {name} Audits
          </Heading>

          {/* 4-Panel Grid Layout */}
          <div className={styles.fourPanelContainer}>
            <div className={styles.fourPanelGrid}>
              {/* Performance Metrics Over Time Chart Panel */}
              <CommunityAuditAccuracyChart auditReports={chartAuditReports} auditSessions={auditSessions} isLoading={chartIsLoading} />

              {/* Performance Metrics Bar Chart Panel */}
              <CommunityAuditPerformanceMetrics comprehensiveAnalytics={comprehensiveAnalytics} isLoading={analyticsComponentsLoading} />

              {/* Label Analysis Panel */}
              <CommunityAuditLabelAnalysis comprehensiveAnalytics={comprehensiveAnalytics} isLoading={analyticsComponentsLoading} />

              {/* Audit History Panel - Now Last */}
              <CommunityAuditProductionStatus
                auditSessions={auditSessions}
                comprehensiveAnalytics={comprehensiveAnalytics}
                isLoading={analyticsComponentsLoading}
              />

              {/* Consolidated Footer for Analytics Panels */}
              <div className={styles.analyticsFooter}>
                <span className={styles.analyticsFooterText}>Averaged across all completed audit reports (max 100)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // V2: Use new CommunityHeader design system component (analytics moved to page body)
  // Uses title-only variant - no subtitle needed for audit pages
  return (
    <CommunityHeader
      v1Component={v1Component}
      v2Props={{
        'data-testid': 'community-audit-header-v2',
        title: `${name} Audits`,
      }}
    />
  );
}

export const CommunityAuditHeaderSection = withAppInsightsTracking(CommunityAuditHeaderSectionComponent, 'CommunityAuditHeaderSection');
